import React, {useEffect, useState, useRef} from 'react';
import {AlertCircle, CheckCircle, Save, Settings, Upload, File, X} from 'lucide-react';
import {getGlobalData, getProjectData, saveProjectData, saveGlobalData} from '../utils/projectStorage';
import './JobGridsSettings.css';

interface JobGridsSettingsData {
  generalInstructions: string;
  sampleJobDescription: string;
}

const JobGridsSettings: React.FC = () => {
  const [settings, setSettings] = useState<JobGridsSettingsData>({
    generalInstructions: `In order to do the work below, you will need two inputs:
A [JOB TITLE]
And A [BRIEF JOB DESCRIPTION].

Your job is to create a Job Grid.

First, analyze this role and identify the 6 core work areas that are both: a) essential to the job's success, and b) areas where the 8 different MCode dimensions would naturally approach the work differently based on their motivational patterns.

Then create a grid with the 8 MCode dimensions (Achiever, Driver, Influencer, Learner, Optimizer, Orchestrator, Relator, Visionary) across the top as columns, and your identified 6 core work areas down the left side as rows.

Fill in each cell showing how each dimension would naturally tackle that specific work area, focusing on their motivational drivers and preferred methods. Each cell should be 1 short sentence describing that dimension's distinct approach.

The goal is to show managers 8 different ways the same job can be performed effectively, demonstrating how to shape roles around people's natural motivational wiring.

Please produce the grid in Markdown.

There are a few samples in your project knowledge.`,
    sampleJobDescription: `Job Title: Card Fraud Data and Analytics Analyst

Job Description:
At Regions, the Data and Analytics Analyst transforms data into a meaningful format for operational and/or decision-making purposes. Focusing on day-to-day deliverables, this role works with senior analysts and managers to gather and interpret data for leaders to make data-driven decisions.

Primary Responsibilities
Modifies reports that are valuable, readable, and presentable to stakeholders
Assists in the preparation of visual reports/presentations to present analysis to management
Interprets, analyzes, and provides insights to teams and managers to determine operational impact, trends, and opportunities
Performs data analysis to help achieve data automation
Creates and maintains databases to help classify & store information
Completes day-to-day deliverables and ad-hoc data reports to support business needs/projects and answer data related questions
Ensures compliance by performing quality data audits, mining, and management
Monitors data and analytics trends and seeks opportunities for continuous improvement
May offer guidance to junior analysts, ensuring proper training and tools are provided
Partners with the centralized Data Governance team to implement and support data governance and data quality requirements
`
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load existing settings
    const projectData = getProjectData('job-grids');
    if (projectData.settings) {
      setSettings({
        generalInstructions: projectData.settings.generalInstructions || '',
        sampleJobDescription: projectData.settings.sampleJobDescription || ''
      });
    }
  }, []);

  const handleInputChange = (field: keyof JobGridsSettingsData, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setMessage('');
    setMessageType('');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['.txt', '.md', '.json'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      setMessage('Please upload a .txt, .md, or .json file');
      setMessageType('error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('File size must be less than 5MB');
      setMessageType('error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      // Save to global data
      const globalData = getGlobalData();
      globalData.motivationalDimensionsFile = {
        name: file.name,
        content,
        lastModified: file.lastModified,
        size: file.size
      };
      saveGlobalData(globalData);
      setMessage('File uploaded successfully to global storage');
      setMessageType('success');
    };
    reader.readAsText(file);
  };

  const removeFile = () => {
    // Remove from global data
    const globalData = getGlobalData();
    globalData.motivationalDimensionsFile = undefined;
    saveGlobalData(globalData);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setMessage('File removed successfully');
    setMessageType('success');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Get current project data and update settings
      const projectData = getProjectData('job-grids');
      projectData.settings = settings;
      
      // Create ingested content similar to Sales Assistant with global motivational dimensions
      const globalData = getGlobalData();
      const motivationalDimensionsContent = globalData.motivationalDimensionsFile?.content || '';

      projectData.ingestedContent = `${settings.generalInstructions || 'Create comprehensive job opportunity grids and career path analysis.'}

Sample Job Description Reference:
${settings.sampleJobDescription || 'No sample job description provided.'}

${motivationalDimensionsContent ? `## Motivational Dimensions Framework
The following is the complete MCode Motivational Dimensions framework that you must use when providing job grid guidance:

${motivationalDimensionsContent}

Please ensure you fully understand and apply these motivational dimensions when tailoring your responses to specific personality types. ` : ''}`;
      
      // Save to storage
      saveProjectData('job-grids', projectData);
      
      setMessage('Settings saved and processed successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings');
      setMessageType('error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="job-grids-settings">
      <div className="settings-header">
        <div className="header-title">
          <Settings size={24} />
          <h1>Job Grids Settings</h1>
        </div>
        <p>Configure settings for Job Grids functionality</p>
      </div>

      <div className="settings-form">
        <div className="form-section">
          <label htmlFor="generalInstructions">General Instructions</label>
          <textarea
            id="generalInstructions"
            placeholder="Enter general instructions for job grid analysis..."
            value={settings.generalInstructions}
            onChange={(e) => handleInputChange('generalInstructions', e.target.value)}
            rows={6}
          />
          <p className="field-description">
            These instructions will guide how AI analyzes and creates job grids.
          </p>
        </div>

        <div className="form-section">
          <label htmlFor="sampleJobDescription">Sample Job Description</label>
          <textarea
            id="sampleJobDescription"
            placeholder="e.g., Senior Software Engineer"
            value={settings.sampleJobDescription}
            onChange={(e) => handleInputChange('sampleJobDescription', e.target.value)}
            rows={6}
          />
          <p className="field-description">
            A sample job description to use as reference for job grid creation.
          </p>
        </div>

        <div className="form-section">
          <label htmlFor="mcodeDimensionsFile">MCode Dimensions File</label>
          
          {(() => {
            const globalData = getGlobalData();
            return globalData.motivationalDimensionsFile ? (
              <div className="file-display">
                <div className="file-info">
                  <File size={20} />
                  <div className="file-details">
                    <span className="file-name">{globalData.motivationalDimensionsFile.name}</span>
                    <span className="file-meta">
                      {formatFileSize(globalData.motivationalDimensionsFile.size)} • 
                      {new Date(globalData.motivationalDimensionsFile.lastModified).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="remove-file-btn"
                    title="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="file-content-preview">
                  <strong>File Content Preview:</strong>
                  <div className="content-preview">
                    {globalData.motivationalDimensionsFile.content.substring(0, 200)}
                    {globalData.motivationalDimensionsFile.content.length > 200 && '...'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="file-upload">
                <input
                  ref={fileInputRef}
                  type="file"
                  id="mcodeDimensionsFile"
                  accept=".txt,.md,.json"
                  onChange={handleFileUpload}
                  className="file-input"
                />
                <label htmlFor="mcodeDimensionsFile" className="file-upload-label">
                  <Upload size={20} />
                  <span>Upload MCode Dimensions File</span>
                  <span className="file-types">Supported: .txt, .md, .json (max 5MB)</span>
                </label>
              </div>
            );
          })()}
          
          <p className="field-description">
            Upload a file containing MCODE dimensional definitions. This file will be shared across all projects.
          </p>
        </div>

        {message && (
          <div className={`message ${messageType}`}>
            {messageType === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            <span>{message}</span>
          </div>
        )}

        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="save-button"
        >
          <Save size={18} />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default JobGridsSettings;